import React from 'react';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { usePopupClose } from '../../Utils/usePopupClose';

/**
 * CriteriaView Component
 * A popup component that displays criteria JSON data
 */
const CriteriaView = ({ 
  show, 
  onClose, 
  criteria_json,
  hiddenAttributes = ["llminstruction"] // Array of keys to exclude from view
}) => {
  // Use the popup close hook to handle clicking outside and ESC key
  const popupRef = usePopupClose(show, onClose);

  // Convert hiddenAttributes array to Set for efficient lookup
  const hiddenAttributesSet = new Set(hiddenAttributes);

  // Filter function to hide specific attributes
  const searchFilter = ({ key, path }) => {
    // Hide attributes that are in the hiddenAttributes set
    const fullPath = path.join('.');
    const keyPath = `${fullPath}.${key}`;
    
    // Check if this specific key or any parent path is hidden
    return !hiddenAttributesSet.has(key) && !hiddenAttributesSet.has(keyPath) && !hiddenAttributesSet.has(fullPath);
  };

  // Don't render anything if the popup shouldn't be shown
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 z-[9999]" style={{ margin: 0, padding: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* Modal Container - True Fullscreen */}
      <div ref={popupRef} className="relative bg-gray-900 flex flex-col" style={{ width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold text-white">Criteria</h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content Area - Takes remaining space */}
        <div className="flex-1 overflow-auto p-6 bg-gray-900">
          <div className="my-4">
            {criteria_json && (
              <JsonEditor
                data={criteria_json}
                setData={() => {}} // No-op function since we don't want editing
                rootName="criteria"
                searchFilter={hiddenAttributes.length > 0 ? searchFilter : undefined}
                theme={[
                  githubDarkTheme,
                  {
                    styles: {
                      container: {
                        backgroundColor: 'transparent'
                      }
                    }
                  }
                ]}
                collapse={5}
                enableClipboard={false}
                showErrorMessages={true}
                showStringQuotes={false}
                showCollectionCount={true}
                restrictEdit={true}
                restrictDelete={true}
                restrictAdd={true}
                restrictTypeSelection={true}
                minWidth="100%"
                maxWidth="100%"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CriteriaView;
